'use client'

import { useEffect, useState, useTransition } from 'react'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'

import { ALL_LOCATIONS } from '@/app/lib/constants'
import {
  GEOLOCATION_MAX_AGE_MS,
  GEOLOCATION_OVERLAY_MAX_MS,
  GEOLOCATION_TIMEOUT_MS,
  LOCATION_PERMISSION_DENIED_KEY,
  getLocationState,
  resolveProvinceFromCoordinates,
  saveDetectedLocation,
} from '@/app/lib/geolocation'

import { waitForCookieBannerClosed } from './waitForCookieBannerClosed'

interface UseGeoLocationSyncOptions {
  /** Location ya resuelta por el Server Component vía `params` (p. ej. 'todos', 'madrid'). */
  location: string
  /** Construye el href localizado para la ruta actual (p. ej. `routes.agenda`). */
  buildLocalizedHref: (citySlug: string) => string
}

/** Estado de permiso relevante para esta feature — `'unsupported'` no es un valor de la Permissions API. */
type GeoPermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported'

async function queryPermissionState(): Promise<GeoPermissionState> {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return 'unsupported'
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' })
    return status.state
  } catch {
    return 'unsupported'
  }
}

function hasStoredDenial(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(LOCATION_PERMISSION_DENIED_KEY) === 'true'
  } catch {
    // Storage bloqueado — no se re-intenta más agresivamente de lo que ya limita la Permissions API (spec.md Edge Case).
    return false
  }
}

function markPermissionDenied(): void {
  try {
    window.localStorage.setItem(LOCATION_PERMISSION_DENIED_KEY, 'true')
  } catch {
    // Storage bloqueado — no hay nada más que hacer; ver hasStoredDenial().
  }
}

/** Cuánto se reutiliza un destino ya resuelto para el mismo pathname bare sin
 * repetir `getLocationState()`/geolocalización (ver `resolvedTargetCache`
 * abajo) — ventana corta a propósito: solo absorbe un remount casi inmediato,
 * nunca sustituye una revisita genuina más tarde en la misma pestaña (otra
 * página pudo haber cambiado `location` entretanto vía `setManualLocation`). */
const RESOLVED_TARGET_TTL_MS = 3000

/**
 * Sobrevive a que React desmonte y remonte este hook dentro de la MISMA carga
 * de página — p. ej. un hydration mismatch ajeno (confirmado en local: el
 * propio Dev Tools Indicator de Next; en producción podría ser cualquier
 * script de terceros o extensión que toque el DOM antes de hidratar) que
 * fuerza "regenerar el árbol en el cliente". Sin esto, cada remount repetía
 * `getLocationState()` + `router.replace()` desde cero, produciendo varias
 * peticiones visibles a la ruta bare antes de asentarse en la localizada (el
 * "bucle" reportado en agenda/promociones). Vive a nivel de MÓDULO, no de
 * instancia/ref: el propio remount destruye cualquier estado de componente.
 *
 * Deliberadamente NO bloquea reintentos: si un intento se cancela antes de
 * resolver un destino, no queda entrada en la caché y el siguiente remount
 * repite el trabajo async completo (nunca deja al visitante varado en
 * "todos"). Solo acelera el camino cuando YA se conoce el destino, reduciendo
 * la ventana en la que otro remount podría interrumpirlo de nuevo.
 */
const resolvedTargetCache = new Map<string, { href: string; resolvedAt: number }>()

/**
 * Invalida toda la caché de destinos ya resueltos. Debe llamarse justo
 * después de persistir una elección MANUAL de provincia (`setManualLocation`)
 * y antes de navegar: sin esto, un remount del mismo pathname bare dentro de
 * `RESOLVED_TARGET_TTL_MS` reproduce el destino de una detección automática
 * ANTERIOR aunque la cookie `location` ya se haya actualizado — el propio
 * comentario de `resolvedTargetCache` asumía que "otra página pudo haber
 * cambiado `location` entretanto vía `setManualLocation`" pero nunca se
 * comprobaba (bug detectado 2026-09-10: limpiar el filtro de provincia justo
 * después de aceptar la geolocalización no surtía efecto si se hacía dentro
 * de esa ventana de 3s). Se limpia la caché entera (no solo la entrada del
 * pathname actual) porque el llamador no siempre conoce el pathname bare
 * exacto que la originó.
 */
export function invalidateResolvedTargetCache(): void {
  resolvedTargetCache.clear()
}

export interface UseGeoLocationSyncResult {
  /** `true` desde que se empieza a comprobar si hace falta redirigir (leer
   * `getLocationState()`, y si procede, pedir permiso/geolocalizar) hasta
   * que se sabe con certeza el resultado: bien porque no hace falta
   * redirigir, bien porque el `router.replace()` hacia la URL localizada ya
   * está en curso. Pensado para que el consumidor pinte un `LoadingOverlay`
   * en las páginas que lo necesiten (p. ej. Home/Promociones, para no
   * mostrar el contenido "todos" sin filtrar ni un instante mientras se
   * decide) — el resto de páginas que no piden overlay simplemente ignoran
   * este valor. */
  isPending: boolean
}

/**
 * Orquesta la detección de geolocalización para una página web-general en
 * alcance (spec.md, contracts/geo-location-sync-component.md). No renderiza
 * nada por sí mismo: su único efecto observable es `router.replace()` hacia
 * la URL localizada, o ningún cambio en absoluto.
 */
export function useGeoLocationSync({
  location,
  buildLocalizedHref,
}: UseGeoLocationSyncOptions): UseGeoLocationSyncResult {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [isReplacing, startTransition] = useTransition()
  // Cubre la comprobación en sí (leer cookies, permiso, geolocalizar), antes
  // de saber si hará falta redirigir — `isReplacing` (de `useTransition`)
  // solo empieza a valer `true` una vez YA se ha decidido que sí hace falta,
  // así que por sí solo dejaría un hueco sin overlay mientras se decide.
  const [isChecking, setIsChecking] = useState(false)

  useEffect(() => {
    // FR-011: la URL ya trae una localización concreta — es la fuente de verdad,
    // no se solicita geolocalización (User Story 4).
    if (location !== ALL_LOCATIONS) return

    // Vía rápida: un remount reciente de este mismo pathname bare ya resolvió
    // el destino (ver `resolvedTargetCache`) — reemplaza directamente, sin
    // repetir la lectura de cookies ni el árbol de decisión de permisos, para
    // no abrir una segunda ventana igual de vulnerable a otro remount.
    const cached = resolvedTargetCache.get(pathname)
    if (cached && Date.now() - cached.resolvedAt < RESOLVED_TARGET_TTL_MS) {
      startTransition(() => {
        router.replace(cached.href)
      })
      return
    }

    let cancelled = false
    // `true` una vez agotado `GEOLOCATION_OVERLAY_MAX_MS` sin decidir: el
    // spinner ya se quitó y el visitante puede estar leyendo la página, así
    // que a partir de aquí no se redirige (daría un salto), solo se guarda.
    let gaveUp = false
    // Corta la espera al banner de cookies si la página se desmonta.
    const abortController = new AbortController()
    // El propio cuerpo del efecto es quien decide, leyendo `pathname`/la
    // caché de módulo, si de verdad hace falta comprobar algo (la vía rápida
    // de arriba ya ha vuelto en caso contrario) — no hay ninguna forma de
    // derivarlo durante el render sin duplicar esa lectura. Mismo caso ya
    // documentado en `CityContext.tsx`/`SessionContext.tsx`: el `setState`
    // directo en el cuerpo del efecto es la excepción legítima, no el "ajusta
    // el estado durante el render" habitual.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsChecking(true)

    // Tope del spinner (`GEOLOCATION_OVERLAY_MAX_MS`). Se pausa mientras se
    // espera al banner de cookies — el visitante lo está leyendo y la página
    // sigue tapada por él — y arranca de nuevo, entero, al pedir la ubicación.
    let overlayCapTimeout: ReturnType<typeof setTimeout> | undefined
    function startOverlayCap(): void {
      clearTimeout(overlayCapTimeout)
      overlayCapTimeout = setTimeout(() => {
        gaveUp = true
        finishChecking()
      }, GEOLOCATION_OVERLAY_MAX_MS)
    }
    startOverlayCap()

    // Se llama en todo punto de salida que concluye "no hace falta
    // redirigir" — a partir de ahí `isReplacing` ya no va a valer `true`
    // nunca para este montaje, así que hay que apagar `isChecking` a mano.
    function finishChecking(): void {
      clearTimeout(overlayCapTimeout)
      if (!cancelled) setIsChecking(false)
    }

    function replaceToLocalized(citySlug: string): void {
      // Tras `gaveUp` la provincia ya quedó guardada en la cookie (si venía
      // de una detección) y se aplicará en la próxima visita.
      if (cancelled || gaveUp) return
      // A partir de aquí el overlay lo sostiene `isReplacing`, no `isChecking`.
      finishChecking()
      const query = searchParams.toString()
      const href = buildLocalizedHref(citySlug)
      const target = query ? `${href}?${query}` : href
      resolvedTargetCache.set(pathname, { href: target, resolvedAt: Date.now() })
      // `startTransition`: marca la navegación como no bloqueante y expone
      // `isReplacing` mientras se resuelve, para el overlay de carga de los
      // consumidores que lo pidan (ver `UseGeoLocationSyncResult`).
      startTransition(() => {
        router.replace(target)
      })
    }

    function detectAndMaybeRedirect(): void {
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        finishChecking() // FR-012: no disponible
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (cancelled) return
          const slug = resolveProvinceFromCoordinates(position.coords.latitude, position.coords.longitude)
          if (!slug) {
            finishChecking() // FR-012: ciudad no resoluble — mantener datos iniciales
            return
          }

          void saveDetectedLocation(slug).then((result) => {
            if (cancelled) return
            if (!result.ok) {
              finishChecking()
              return
            }
            replaceToLocalized(slug) // FR-007–FR-010
          })
        },
        (error) => {
          if (cancelled) return
          if (error.code === error.PERMISSION_DENIED) {
            markPermissionDenied() // FR-006
          }
          // Cualquier otro error o timeout: no-op silencioso, sin marcar el flag (FR-012).
          finishChecking()
        },
        { timeout: GEOLOCATION_TIMEOUT_MS, maximumAge: GEOLOCATION_MAX_AGE_MS },
      )
    }

    async function run(): Promise<void> {
      // `location` (no `location_geo`) es la fuente única de verdad para la
      // navegación: la escriben tanto una detección exitosa (`saveDetectedLocation`,
      // sincroniza ambas cookies) como una elección manual en el filtro de
      // recintos/agenda/lo-más-vendido (`setManualLocation`, solo `location`). Si ya
      // hay una ubicación conocida, se respeta y se redirige sin tocar permisos ni
      // geolocalización — la elección manual más reciente nunca queda tapada por
      // una detección antigua (spec.md FR-017/FR-023, revisado tras confirmar que
      // el selector manual real no tocaba ninguna cookie).
      const { locationSlug, locationGeoSlug } = await getLocationState()
      if (cancelled) return

      if (locationSlug !== ALL_LOCATIONS) {
        replaceToLocalized(locationSlug)
        return
      }

      // `locationSlug === ALL_LOCATIONS` a partir de aquí. `src/proxy.ts` ya
      // garantiza que la cookie `location` SIEMPRE existe (la crea con
      // 'todos' de base en la primera visita), así que "vale 'todos'" no
      // distingue por sí solo "nunca se ha elegido nada" de "el usuario
      // limpió el filtro a propósito". `location_geo` sí lo distingue:
      // `setManualLocation` (el filtro manual) nunca la toca, así que si
      // sigue reflejando una detección real mientras `location` ya está en
      // 'todos', es que el usuario limpió el filtro deliberadamente después
      // de una detección — se respeta esa elección y NO se vuelve a
      // detectar, aunque el permiso siga concedido y la geolocalización real
      // sea otra distinta.
      if (locationGeoSlug) {
        finishChecking()
        return
      }

      // Ni `location` ni `location_geo` reflejan ninguna ubicación conocida
      // todavía — a partir de aquí sí entra en juego el permiso, para
      // decidir si procede detectar por primera vez.
      const permissionState = await queryPermissionState()
      if (cancelled) return

      if (permissionState === 'denied') {
        finishChecking() // FR-005: no se solicita automáticamente
        return
      }

      if (permissionState === 'granted') {
        detectAndMaybeRedirect() // ya concedido, sin nuevo prompt
        return
      }

      // 'prompt' | 'unsupported'
      if (hasStoredDenial()) {
        finishChecking() // FR-005/FR-006
        return
      }

      // El aviso de permiso del navegador se queda con el foco: con el banner
      // de cookies abierto a la vez, el primer clic en él no llegaba al botón.
      // Se pide la ubicación cuando el banner ya se ha cerrado.
      clearTimeout(overlayCapTimeout)
      await waitForCookieBannerClosed(abortController.signal)
      if (cancelled) return
      startOverlayCap()
      detectAndMaybeRedirect() // puede disparar el prompt del navegador (FR-003)
    }

    void run()

    return () => {
      cancelled = true
      clearTimeout(overlayCapTimeout)
      abortController.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- router/pathname/searchParams/buildLocalizedHref son estables para esta página; solo un cambio real de `location` (navegación) debe re-disparar la detección — incluir searchParams re-ejecutaría la detección en cada cambio de filtro.
  }, [location])

  return { isPending: isChecking || isReplacing }
}
